import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Edit, Trash2, Calendar, ClipboardList, MapPin, Users as UsersIcon, DollarSign, Award, Building2, ArrowUpCircle, FileSpreadsheet } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import AdminSidebar from '../components/AdminSidebar';
import ClassEnrollmentsModal from '../components/ClassEnrollmentsModal';
import type { Class } from '../types/class';

interface EnrollmentWithDetails {
  id: string;
  user_id: string;
  class_id: string;
  status: 'ACTIVE' | 'CANCELLED';
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    email: string;
    name?: string;
    phone?: string;
  };
  class?: {
    id: string;
    title: string;
    thumbnail_url: string | null;
    start_date: string;
    end_date: string;
    status: string;
  };
}

export default function ProviderPostsPage() {
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrollmentsModalOpen, setEnrollmentsModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState<{ id: string; title: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'classes' | 'students'>('classes');
  const [students, setStudents] = useState<EnrollmentWithDetails[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [bumpingId, setBumpingId] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  // 비로그인 또는 교육업체가 아니면 리다이렉트
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (user?.role !== 'provider' && user?.role !== 'admin') {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // 수업 목록 조회
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await fetch('/api/v1/provider/classes', {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('수업 목록 조회에 실패했습니다.');
        }

        const data = await response.json();
        setClasses(data.classes || []);
      } catch (err: any) {
        console.error('Failed to fetch classes:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchClasses();
    }

    // 모집중/진행중 등 상태는 서버에서 날짜 기준으로 매번 다시 계산되므로,
    // 탭을 오래 열어둔 채 방치하면 화면이 최초 로드 시점 상태로 굳어버림 → 포커스 복귀 시 재조회
    const handleVisibility = () => {
      if (isAuthenticated && document.visibilityState === 'visible') {
        fetchClasses();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [isAuthenticated]);

  // 수강생 목록 로드
  useEffect(() => {
    const fetchStudents = async () => {
      if (!user?.id) return;

      try {
        setStudentsLoading(true);
        const response = await fetch('/api/v1/provider/enrollments', {
          credentials: 'include',
        });

        if (!response.ok) {
          console.error('Failed to fetch enrollments');
          setStudents([]);
          return;
        }

        const data = await response.json();
        setStudents(data.enrollments || []);
      } catch (err) {
        console.error('Failed to fetch enrollments:', err);
        setStudents([]);
      } finally {
        setStudentsLoading(false);
      }
    };

    if (user) {
      fetchStudents();
    }

    const handleVisibility = () => {
      if (user && document.visibilityState === 'visible') {
        fetchStudents();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [user]);

  const handleDelete = async (classId: string) => {
    if (!confirm('정말 이 수업을 삭제하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/provider/classes/${classId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('수업 삭제에 실패했습니다.');
      }

      // 목록에서 제거
      setClasses((prev) => prev.filter((c) => c.id !== classId));
    } catch (err: any) {
      console.error('Failed to delete class:', err);
      alert(err.message);
    }
  };

  const handleStatusChange = async (classId: string, newStatus: string) => {
    try {
      const response = await fetch(`/api/v1/provider/classes/${classId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('상태 변경에 실패했습니다.');
      }

      setClasses((prev) =>
        prev.map((c) => (c.id === classId ? { ...c, status: newStatus as any } : c))
      );
    } catch (err: any) {
      console.error('Failed to update status:', err);
      alert(err.message);
    }
  };

  const handleClosingSoonToggle = async (classId: string, currentValue: boolean) => {
    try {
      const response = await fetch(`/api/v1/provider/classes/${classId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ closingSoon: !currentValue }),
      });

      if (!response.ok) {
        throw new Error('마감 임박 설정에 실패했습니다.');
      }

      setClasses((prev) =>
        prev.map((c) => (c.id === classId ? { ...c, closing_soon: !currentValue } : c))
      );
    } catch (err: any) {
      console.error('Failed to toggle closing soon:', err);
      alert(err.message);
    }
  };

  const BUMP_COOLDOWN_MS = 5 * 24 * 60 * 60 * 1000;

  const getBumpState = (cls: Class) => {
    const last = new Date(cls.bumped_at || cls.created_at).getTime();
    const remaining = BUMP_COOLDOWN_MS - (nowMs - last);
    if (remaining <= 0) return { ready: true as const, label: '끌어올리기' };
    const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
    const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    if (days > 0) return { ready: false as const, label: `${days}일 ${hours}시간 후` };
    if (hours > 0) return { ready: false as const, label: `${hours}시간 후` };
    return { ready: false as const, label: '1시간 이내' };
  };

  const handleBump = async (classId: string) => {
    try {
      setBumpingId(classId);
      const response = await fetch(`/api/v1/provider/classes/${classId}/bump`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || '끌어올리기에 실패했습니다.');
      }
      setClasses((prev) =>
        prev
          .map((c) => (c.id === classId ? { ...c, bumped_at: data.bumped_at } : c))
          .sort(
            (a, b) =>
              new Date(b.bumped_at || b.created_at).getTime() -
              new Date(a.bumped_at || a.created_at).getTime(),
          ),
      );
    } catch (err: any) {
      console.error('Failed to bump class:', err);
      alert(err.message);
    } finally {
      setBumpingId(null);
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, { text: string; color: string }> = {
      DRAFT: { text: '임시저장', color: 'bg-gray-100 text-gray-700' },
      SCHEDULED: { text: '모집예정', color: 'bg-blue-100 text-blue-700' },
      RECRUITING: { text: '모집중', color: 'bg-green-100 text-green-700' },
      CLOSED: { text: '모집마감', color: 'bg-red-100 text-red-700' },
      IN_PROGRESS: { text: '진행중', color: 'bg-blue-100 text-blue-700' },
      COMPLETED: { text: '종료', color: 'bg-gray-100 text-gray-700' },
      CANCELLED: { text: '취소', color: 'bg-red-100 text-red-700' },
    };
    return labels[status] || labels.DRAFT;
  };

  if (!isAuthenticated || (user?.role !== 'provider' && user?.role !== 'admin')) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
        {/* 공유 사이드바 */}
        <AdminSidebar />

        {/* 메인 콘텐츠 */}
        <main className="flex-1 min-w-0 p-6 sm:px-10 lg:px-16">
          <div className="max-w-6xl mx-auto">
            {/* 헤더 */}
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-primary" />
                  수업 목록
                </h1>
                <p className="text-gray-500 mt-1">
                  등록한 수업 및 교육 프로그램을 관리하세요.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/provider/classes/create-past')}
                  className="px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors flex items-center gap-2"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  과거 수업 등록
                </button>
                <button
                  onClick={() => navigate('/provider/classes/create')}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  수업 등록
                </button>
              </div>
            </div>

            {/* 탭 네비게이션 */}
            <div className="bg-white rounded-t-xl shadow-soft border-b border-gray-200 mb-6">
              <div className="flex gap-1 p-1">
                <button
                  onClick={() => setActiveTab('classes')}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'classes'
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Building2 className="w-4 h-4" />
                  수업 목록 ({classes.length})
                </button>
                <button
                  onClick={() => setActiveTab('students')}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'students'
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <UsersIcon className="w-4 h-4" />
                  수강생 목록 ({students.length})
                </button>
              </div>
            </div>

            {/* 수업 목록 탭 */}
            {activeTab === 'classes' && (
              <>
                {/* 에러 메시지 */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                {/* 로딩 */}
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  </div>
                ) : classes.length === 0 ? (
                  /* 빈 상태 */
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      등록된 수업이 없습니다
                    </h3>
                    <p className="text-gray-500 mb-6">
                      첫 번째 교육 프로그램을 등록해보세요.
                    </p>
                    <button
                      onClick={() => navigate('/provider/classes/create')}
                      className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors inline-flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      수업 등록하기
                    </button>
                  </div>
                ) : (
                  /* 수업 목록 */
                  <div className="space-y-4">
                    {classes.map((cls) => {
                      const statusLabel = getStatusLabel(cls.status);
                      return (
                        <div
                          key={cls.id}
                          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start gap-4">
                            {/* 썸네일 */}
                            <div
                              className="w-72 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setSelectedClass({ id: cls.id, title: cls.title });
                                setEnrollmentsModalOpen(true);
                              }}
                            >
                              {cls.thumbnail_url ? (
                                <img
                                  src={cls.thumbnail_url}
                                  alt={cls.title}
                                  className="w-full h-auto rounded-lg"
                                />
                              ) : (
                                <div className="w-full aspect-[4/3] flex items-center justify-center text-gray-400">
                                  <FileText className="w-8 h-8" />
                                </div>
                              )}
                            </div>

                            {/* 수업 정보 */}
                            <div
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => {
                                setSelectedClass({ id: cls.id, title: cls.title });
                                setEnrollmentsModalOpen(true);
                              }}
                            >
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-xl font-bold text-gray-900">{cls.title}</h3>
                                <span
                                  className={`px-3 py-1 rounded-full text-xs font-medium ${statusLabel.color}`}
                                >
                                  {statusLabel.text}
                                </span>
                              </div>

                              <div className="grid grid-cols-2 gap-4 mt-4 text-sm text-gray-600">
                                <div className="flex items-start gap-2">
                                  <ClipboardList className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-400">모집기간</p>
                                    <p className="whitespace-nowrap">
                                      {/* 과거 수업(가격 0)은 모집기간 미입력 → "-" */}
                                      {cls.price === 0 ? '-' : new Date(cls.enrollment_start_date).toLocaleDateString('ko-KR')} ~{' '}
                                      {cls.price === 0 ? '' : new Date(cls.enrollment_end_date).toLocaleDateString('ko-KR')}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2">
                                  <Calendar className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-400">수업기간</p>
                                    <p className="whitespace-nowrap">
                                      {/* 과거 수업(가격 0)은 시작일 미입력 → "-" */}
                                      {cls.price === 0 ? '-' : new Date(cls.start_date).toLocaleDateString('ko-KR')} ~{' '}
                                      {new Date(cls.end_date).toLocaleDateString('ko-KR')}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-gray-400" />
                                  <span>
                                    {cls.location_type === 'ONLINE'
                                      ? '온라인'
                                      : cls.location_type === 'OFFLINE'
                                      ? cls.location_address || '-'
                                      : '온/오프라인'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <UsersIcon className="w-4 h-4 text-gray-400" />
                                  <span>
                                    {cls.current_count} / {cls.capacity}명
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <DollarSign className="w-4 h-4 text-gray-400" />
                                  <span>{cls.price > 0 ? `${cls.price.toLocaleString()}원` : '-'}</span>
                                </div>
                              </div>

                              {/* 모집 상태 변경 & 마감 임박 & 끌어올리기 */}
                              {cls.status !== 'IN_PROGRESS' && cls.status !== 'COMPLETED' && cls.status !== 'DRAFT' && cls.status !== 'CANCELLED' && (
                                <div
                                  className="flex items-center gap-3 mt-4 pt-3 border-t border-gray-100 flex-wrap"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <span className="text-xs text-gray-500 font-medium whitespace-nowrap">모집 상태 변경</span>
                                  <div className="flex gap-1.5">
                                    {(['SCHEDULED', 'RECRUITING', 'CLOSED'] as const).map((s) => {
                                      const labels = { SCHEDULED: '모집예정', RECRUITING: '모집중', CLOSED: '모집마감' };
                                      const isActive = cls.status === s;
                                      const colors = {
                                        SCHEDULED: isActive ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-100',
                                        RECRUITING: isActive ? 'bg-green-500 text-white' : 'bg-green-50 text-green-600 hover:bg-green-100',
                                        CLOSED: isActive ? 'bg-red-500 text-white' : 'bg-red-50 text-red-600 hover:bg-red-100',
                                      };
                                      return (
                                        <button
                                          key={s}
                                          onClick={() => !isActive && handleStatusChange(cls.id, s)}
                                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${colors[s]} ${isActive ? 'cursor-default' : 'cursor-pointer'}`}
                                        >
                                          {labels[s]}
                                        </button>
                                      );
                                    })}
                                  </div>

                                  {cls.status === 'RECRUITING' && (() => {
                                    const bumpState = getBumpState(cls);
                                    const disabled = !bumpState.ready || bumpingId === cls.id;
                                    return (
                                      <button
                                        onClick={() => bumpState.ready && handleBump(cls.id)}
                                        disabled={disabled}
                                        title={bumpState.ready ? '게시물을 목록 상단으로 끌어올립니다 (5일에 1회)' : `다음 끌어올리기 가능 시점: ${bumpState.label}`}
                                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                          bumpState.ready
                                            ? 'bg-accent text-white hover:bg-accent/90 cursor-pointer'
                                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        }`}
                                      >
                                        <ArrowUpCircle className="w-3.5 h-3.5" />
                                        {bumpingId === cls.id ? '처리 중...' : bumpState.label}
                                      </button>
                                    );
                                  })()}
                                </div>
                              )}

                              {/* 모집 마감 */}
                              {cls.status !== 'IN_PROGRESS' && cls.status !== 'COMPLETED' && cls.status !== 'DRAFT' && cls.status !== 'CANCELLED' && (
                                <div
                                  className="flex items-center gap-3 mt-2 pt-3 border-t border-gray-100 flex-wrap"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <span className="text-xs text-gray-500 font-medium whitespace-nowrap">모집 마감</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600 font-medium">마감 임박</span>
                                    <button
                                      onClick={() => handleClosingSoonToggle(cls.id, cls.closing_soon)}
                                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 ${
                                        cls.closing_soon ? 'bg-red-500' : 'bg-gray-300'
                                      }`}
                                    >
                                      <span
                                        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
                                          cls.closing_soon ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                      />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* 액션 버튼 */}
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/provider/classes/${cls.id}/certificates`);
                                }}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                                title="인증서 발급"
                              >
                                <Award className="w-5 h-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/provider/classes/${cls.id}/edit`);
                                }}
                                className="p-2 text-gray-600 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors"
                                title="수정"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(cls.id);
                                }}
                                className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="삭제"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* 안내 메시지 */}
                {!loading && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h3 className="font-medium text-blue-800">수업 관리 안내</h3>
                    <ul className="mt-2 text-sm text-blue-700 space-y-1">
                      <li>• 수업 카드를 클릭하면 수강 신청자 목록을 확인할 수 있습니다.</li>
                      <li>• 수업 상태는 날짜에 따라 자동으로 변경됩니다.</li>
                      <li>• <strong>끌어올리기</strong>는 모집중 수업을 5일에 한 번씩 목록 상단으로 올릴 수 있습니다.</li>
                      <li>• 우수 인증 교육 기관는 메인 배너에 노출되어 더 많은 학생을 모집할 수 있습니다.</li>
                      <li>• 수업은 메인 페이지 및 검색 결과에 노출됩니다.</li>
                    </ul>
                  </div>
                )}
              </>
            )}

            {/* 수강생 목록 탭 */}
            {activeTab === 'students' && (
              <div className="bg-white rounded-b-xl shadow-soft p-6">
                {/* 검색 바 */}
                <div className="mb-6">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="이름, 이메일, 전화번호로 검색..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                    <UsersIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    전체 수강생 {students.length}명
                  </p>
                </div>

                {/* 수강생 목록 */}
                {studentsLoading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="animate-pulse h-24 bg-gray-200 rounded-lg"></div>
                    ))}
                  </div>
                ) : students.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            수강생
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            연락처
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            수업명
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            수강 기간
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            신청일
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                            상태
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {students
                          .filter((enrollment) => {
                            if (!searchQuery) return true;
                            const query = searchQuery.toLowerCase();
                            return (
                              enrollment.user?.name?.toLowerCase().includes(query) ||
                              enrollment.user?.email?.toLowerCase().includes(query) ||
                              enrollment.user?.phone?.includes(query)
                            );
                          })
                          .map((enrollment) => (
                            <tr
                              key={enrollment.id}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <td className="px-4 py-4">
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {enrollment.user?.name || '이름 없음'}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {enrollment.user?.email}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-700">
                                {enrollment.user?.phone || '-'}
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-2">
                                  {enrollment.class?.thumbnail_url && (
                                    <img
                                      src={enrollment.class.thumbnail_url}
                                      alt={enrollment.class.title}
                                      className="w-10 h-10 rounded object-cover"
                                    />
                                  )}
                                  <span className="font-medium text-gray-900 text-sm">
                                    {enrollment.class?.title || '수업 정보 없음'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-700">
                                {enrollment.class?.start_date && enrollment.class?.end_date ? (
                                  <div>
                                    <div>{new Date(enrollment.class.start_date).toLocaleDateString('ko-KR')}</div>
                                    <div className="text-xs text-gray-500">
                                      ~ {new Date(enrollment.class.end_date).toLocaleDateString('ko-KR')}
                                    </div>
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-700">
                                {new Date(enrollment.created_at).toLocaleDateString('ko-KR')}
                              </td>
                              <td className="px-4 py-4">
                                {enrollment.class?.status === 'COMPLETED' ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                    수강 완료
                                  </span>
                                ) : enrollment.class?.status === 'CANCELLED' ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                    수업 취소됨
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    수강 중
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <UsersIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg mb-2">등록된 수강생이 없습니다</p>
                    <p className="text-gray-400 text-sm">
                      수업에 수강신청이 들어오면 여기에 표시됩니다
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
      <Footer />

      {/* Enrollment Modal */}
      {selectedClass && (
        <ClassEnrollmentsModal
          isOpen={enrollmentsModalOpen}
          onClose={() => {
            setEnrollmentsModalOpen(false);
            setSelectedClass(null);
          }}
          classId={selectedClass.id}
          classTitle={selectedClass.title}
        />
      )}
    </div>
  );
}
